"use client";

import React, { useState } from "react";
import { Button } from "../button";
import { Badge } from "../badge";
import { Bug, ChevronDown, ChevronUp, Copy, Download, Eye, EyeOff } from "lucide-react";
import { CalendarEvent } from "../../types/calendar";

interface EventDiagnosticsTabProps {
  event: CalendarEvent;
}

export function EventDiagnosticsTab({ event }: EventDiagnosticsTabProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSensitiveData, setShowSensitiveData] = useState(false);

  const extractComponentProps = (obj: any, maxDepth = 3, currentDepth = 0): any => {
    if (currentDepth >= maxDepth || obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.slice(0, 5).map(item => extractComponentProps(item, maxDepth, currentDepth + 1));
    }

    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (!showSensitiveData && (key.startsWith('_') || key === 'password' || key === 'token' || key.includes('secret'))) {
        result[key] = '[HIDDEN]';
      } else {
        result[key] = extractComponentProps(value, maxDepth, currentDepth + 1);
      }
    }
    
return result;
  };

  const diagnosticData = {
    event: extractComponentProps(event, 5),
    timestamp: new Date().toISOString(),
    userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'Server',
    environment: {
      nodeEnv: process.env.NODE_ENV,
      isClient: typeof window !== 'undefined',
      isServer: typeof window === 'undefined',
    },
    metadata: {
      prop: `Event Details for ${event.title}`,
      eventSource: event.source,
      hasAttendees: !!(event.attendees && event.attendees.length > 0),
      hasLocation: !!event.location,
      hasDescription: !!event.description,
    }
  };

  const handleCopyData = () => {
    navigator.clipboard.writeText(JSON.stringify(diagnosticData, null, 2));
  };

  const handleDownloadData = () => {
    const dataStr = JSON.stringify(diagnosticData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `event-diagnostics-${event.id || 'unknown'}-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (process.env.NODE_ENV === 'production') {
    return (
      <div className="text-center py-8">
        <Bug className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">
          Diagnostic information is only available in development mode.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Bug className="h-6 w-6 text-muted-foreground" />
          <div>
            <h3 className="font-semibold text-lg">Diagnostic Information</h3>
            <p className="text-sm text-muted-foreground">
              Development debugging tools and event data
            </p>
          </div>
        </div>
        <Badge variant="secondary" className="bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
          Dev Only
        </Badge>
      </div>

      {/* Controls */}
      <div className="flex items-center space-x-3 p-4 bg-muted/30 rounded-lg">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center space-x-2"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-4 w-4" />
              <span>Hide Details</span>
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              <span>Show Details</span>
            </>
          )}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowSensitiveData(!showSensitiveData)}
          className="flex items-center space-x-2"
        >
          {showSensitiveData ? (
            <>
              <EyeOff className="h-4 w-4" />
              <span>Hide Sensitive</span>
            </>
          ) : (
            <>
              <Eye className="h-4 w-4" />
              <span>Show Sensitive</span>
            </>
          )}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={handleCopyData}
          className="flex items-center space-x-2"
        >
          <Copy className="h-4 w-4" />
          <span>Copy Data</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={handleDownloadData}
          className="flex items-center space-x-2"
        >
          <Download className="h-4 w-4" />
          <span>Download</span>
        </Button>
      </div>

      {/* Event Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 bg-muted/30 rounded-lg">
          <div className="text-sm font-medium mb-1">Event Source</div>
          <Badge className="capitalize">{event.source}</Badge>
        </div>
        
        <div className="p-4 bg-muted/30 rounded-lg">
          <div className="text-sm font-medium mb-1">Has Attendees</div>
          <Badge variant={event.attendees?.length ? "default" : "secondary"}>
            {event.attendees?.length || 0}
          </Badge>
        </div>
        
        <div className="p-4 bg-muted/30 rounded-lg">
          <div className="text-sm font-medium mb-1">Has Location</div>
          <Badge variant={event.location ? "default" : "secondary"}>
            {event.location ? "Yes" : "No"}
          </Badge>
        </div>
        
        <div className="p-4 bg-muted/30 rounded-lg">
          <div className="text-sm font-medium mb-1">Has Description</div>
          <Badge variant={event.description ? "default" : "secondary"}>
            {event.description ? "Yes" : "No"}
          </Badge>
        </div>
      </div>

      {/* Detailed Data */}
      {isExpanded && (
        <div className="space-y-4">
          <h4 className="font-medium text-sm">Raw Event Data</h4>
          <div className="bg-background border rounded-lg p-4">
            <pre className="text-xs overflow-auto max-h-96 whitespace-pre-wrap">
              {JSON.stringify(diagnosticData, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* Environment Info */}
      <div className="space-y-4">
        <h4 className="font-medium text-sm">Environment Information</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="text-xs text-muted-foreground mb-1">Environment</div>
            <div className="text-sm font-medium">{diagnosticData.environment.nodeEnv}</div>
          </div>
          
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="text-xs text-muted-foreground mb-1">Runtime</div>
            <div className="text-sm font-medium">
              {diagnosticData.environment.isClient ? 'Client' : 'Server'}
            </div>
          </div>
          
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="text-xs text-muted-foreground mb-1">Timestamp</div>
            <div className="text-sm font-medium">{diagnosticData.timestamp}</div>
          </div>
          
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="text-xs text-muted-foreground mb-1">Event ID</div>
            <div className="text-sm font-mono">{event.id || 'No ID'}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
